import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import RelatedList from './components/RelatedList';
import { useFavorites } from '@/hooks/useFavorites';
import {
  getCategoryName,
  getResourceById,
  resources,
} from '@/mocks/resources';
import { formatDate, formatViews } from '@/utils/format';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function Detail() {
  const { id = '' } = useParams();
  const resource = getResourceById(id);
  const { isFavorite, toggle } = useFavorites();
  const [toast, setToast] = useState('');

  const related = useMemo(() => {
    if (!resource) return [];
    return resources
      .filter((item) => item.category === resource.category && item.id !== resource.id)
      .slice(0, 4);
  }, [resource]);

  usePageMeta(
    resource
      ? `${resource.title} - 国语对白做受69影视详情`
      : '资源不存在 - 国语对白做受69',
    resource
      ? `${resource.synopsis.slice(0, 70)}… 《${resource.title}》${resource.region}${resource.year}年${resource.genres.join('、')}类国语对白影音资源详情。`
      : '你访问的影音资源不存在，请返回首页浏览其他国语对白影音资源。',
    resource
      ? `${resource.title},${resource.genres.join(',')},${resource.region},国语对白做受69`
      : '国语对白做受69,影音资源'
  );

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2200);
  };

  const handleFavorite = () => {
    if (!resource) return;
    toggle(resource.id);
    showToast(isFavorite(resource.id) ? '已取消收藏' : '已加入收藏（本地保存演示）');
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast('分享链接已复制到剪贴板');
    } catch {
      showToast('复制失败，请手动复制地址栏链接');
    }
  };

  if (!resource) {
    return (
      <div className="flex min-h-screen flex-col bg-background-100">
        <Navbar />
        <main className="mx-auto flex w-full max-w-[1360px] flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-500">
            <i className="ri-file-unknow-line" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            资源不存在
          </h1>
          <p className="mt-2 max-w-md text-sm text-foreground-600">
            你访问的影音资源可能已被移除或链接有误，返回首页继续浏览其他内容。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex h-10 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const favorited = isFavorite(resource.id);

  const infoRows = [
    { label: '导演', value: resource.director },
    { label: '主演', value: resource.cast.join('、') },
    { label: '类型', value: resource.genres.join(' / ') },
    { label: '地区', value: resource.region },
    { label: '上映/首播', value: formatDate(resource.publishDate) },
    { label: '时长', value: resource.duration },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background-100">
      <Navbar />
      <main className="mx-auto w-full max-w-[1360px] flex-1 px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: getCategoryName(resource.category), to: `/category/${resource.category}` },
            { label: resource.title },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[300px_1fr]">
          <div>
            <div className="w-full max-w-[300px] overflow-hidden rounded-lg border border-background-200 bg-background-50 lg:max-w-none">
              <div className="aspect-[2/3] w-full bg-background-200">
                <img
                  src={resource.cover}
                  alt={`${resource.title} 国语对白高清影音封面`}
                  title={`${resource.title} 影视详情封面`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="mt-4 flex gap-3">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex h-11 flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-label transition-colors ${
                  favorited
                    ? 'bg-accent-500 text-foreground-950'
                    : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                }`}
              >
                <i className={favorited ? 'ri-heart-fill' : 'ri-heart-line'} />
                {favorited ? '已收藏' : '收藏'}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex h-11 flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 text-sm font-label text-foreground-800 transition-colors hover:bg-background-100"
              >
                <i className="ri-share-forward-line" />
                分享
              </button>
            </div>
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/category/${resource.category}`}
                className="inline-flex cursor-pointer items-center gap-1 rounded-md bg-primary-100 px-2.5 py-1 text-xs font-label text-primary-700 transition-colors hover:bg-primary-200"
              >
                <i className="ri-price-tag-3-line" />
                {getCategoryName(resource.category)}
              </Link>
              {resource.genres.map((genre) => (
                <span
                  key={genre}
                  className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-label text-secondary-900"
                >
                  {genre}
                </span>
              ))}
            </div>

            <h1 className="mt-3 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
              {resource.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3">
              <div className="flex items-center gap-2">
                <span className="font-heading text-3xl font-bold text-accent-600">
                  {resource.rating.toFixed(1)}
                </span>
                <div className="flex flex-col">
                  <span className="flex text-accent-500">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={
                          i < Math.round(resource.rating / 2)
                            ? 'ri-star-fill'
                            : 'ri-star-line'
                        }
                      />
                    ))}
                  </span>
                  <span className="text-xs text-foreground-500">综合评分</span>
                </div>
              </div>
              <span className="flex items-center gap-1.5 text-sm text-foreground-600">
                <i className="ri-eye-line text-base" />
                {formatViews(resource.views)} 次浏览
              </span>
              <span className="flex items-center gap-1.5 text-sm text-foreground-600">
                <i className="ri-calendar-line text-base" />
                {resource.year} 年
              </span>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                剧情简介
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
                {resource.synopsis}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                本站收录的《{resource.title}》为国语对白影音资源，内容信息仅供检索与浏览参考。
              </p>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">
                作品信息
              </h2>
              <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <dt className="w-16 shrink-0 text-foreground-500">
                      {row.label}
                    </dt>
                    <dd className="flex-1 text-foreground-900">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3 rounded-lg border border-accent-300 bg-accent-100/60 px-5 py-4">
              <i className="ri-information-line text-lg text-accent-700" />
              <p className="text-sm text-accent-900">
                收藏功能为本地存储演示，仅保存在当前浏览器中，不涉及账户体系；分享将复制当前页面链接。
              </p>
            </div>
          </div>
        </div>

        <RelatedList items={related} />
      </main>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[70] -translate-x-1/2 rounded-full bg-foreground-950 px-5 py-2.5 text-sm font-label text-background-50 shadow-none">
          {toast}
        </div>
      )}

      <Footer />
    </div>
  );
}